import {useCallback} from 'react'
import styles from './PermissionDisplay.module.css'
import {InlineCode} from './InlineCode'
import {UserChoiceCard} from './UserChoiceCard'
import {ChoiceButton, ChoiceButtonGroup} from './ChoiceButton'

export interface PermissionDisplayProps {
  /** The kind of permission: 'commands', 'shell', 'write', 'mcp', or a tool name */
  kind: string
  /** The tool requesting permission */
  toolName?: string
  /** Name of the MCP server providing the tool (for kind: "mcp") */
  serverName?: string
  /** What the tool intends to do */
  intention?: string
  /** Command text or filename to display */
  detail?: string
  /** Whether session-level approval can be offered */
  canOfferSessionApproval?: boolean
  /** Command identifiers for session-level approval label */
  commandIdentifiers?: string[]
  /** Callback when user responds (approved, scope) */
  onRespond?: (approved: boolean, scope?: 'once' | 'session') => void
  /** Whether interaction is disabled (e.g., while submitting) */
  disabled?: boolean
  /** When true, hides container chrome (for embedding in another container) */
  embedded?: boolean
}

const EMPTY_COMMAND_IDENTIFIERS: string[] = []

/** Display a permission request prompt with Allow/Deny choices */
export function PermissionDisplay({
  kind,
  toolName,
  serverName,
  intention,
  detail,
  canOfferSessionApproval = false,
  commandIdentifiers = EMPTY_COMMAND_IDENTIFIERS,
  onRespond,
  disabled = false,
  embedded = false,
}: PermissionDisplayProps) {
  const handleAllow = useCallback(() => {
    if (disabled) return
    onRespond?.(true)
  }, [disabled, onRespond])

  const handleAllowSession = useCallback(() => {
    if (disabled) return
    onRespond?.(true, 'session')
  }, [disabled, onRespond])

  const handleDeny = useCallback(() => {
    if (disabled) return
    onRespond?.(false)
  }, [disabled, onRespond])

  const isCommand = kind === 'commands' || kind === 'shell'
  const isWrite = kind === 'write'
  const isMcp = kind === 'mcp'
  const label = isCommand
    ? 'Run command'
    : isWrite
      ? 'Edit file'
      : isMcp
        ? `MCP tool: ${serverName ? `${serverName}/` : ''}${toolName ?? 'unknown'}`
        : `Permission: ${toolName ?? kind}`

  const sessionLabel =
    canOfferSessionApproval && commandIdentifiers.length > 0
      ? `Allow and don't ask again for \`${commandIdentifiers.join('`, `')}\``
      : isWrite
        ? 'Allow all edits this session'
        : isMcp && toolName
          ? `Allow \`${toolName}\` for this session`
          : 'Allow for session'

  // MCP tools always offer session-level approval
  const showSessionOption = canOfferSessionApproval || isWrite || isMcp

  let choiceIndex = 1

  return (
    <UserChoiceCard
      title={intention || label}
      embedded={embedded}
      previewContent={detail ? <div className={styles.detail}>{detail}</div> : undefined}
    >
      <ChoiceButtonGroup>
        <ChoiceButton number={choiceIndex++} label="Allow" disabled={disabled} onClick={handleAllow} />
        {showSessionOption && (
          <ChoiceButton
            number={choiceIndex++}
            label={<InlineCode text={sessionLabel} codeClassName={styles.sessionLabelCode} />}
            disabled={disabled}
            onClick={handleAllowSession}
          />
        )}
        <ChoiceButton number={choiceIndex} label="Deny" disabled={disabled} onClick={handleDeny} />
      </ChoiceButtonGroup>
    </UserChoiceCard>
  )
}
